import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import {
  FAIR_VARIANT_COMPARE_MAX,
  type FairVariantCompareItem,
} from '@/ui/tokens/fair-variant-compare.data'

const STORAGE_KEY = 'fair-variant-compare'

function readStoredItems(): FairVariantCompareItem[] {
  if (typeof window === 'undefined') {
    return []
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return []
    }
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.filter(isCompareItem).slice(0, FAIR_VARIANT_COMPARE_MAX)
  } catch {
    return []
  }
}

function isCompareItem(value: unknown): value is FairVariantCompareItem {
  if (!value || typeof value !== 'object') {
    return false
  }
  const item = value as FairVariantCompareItem
  return (
    typeof item.productId === 'number'
    && typeof item.variantId === 'number'
    && typeof item.brand === 'string'
    && typeof item.title === 'string'
    && typeof item.price === 'string'
  )
}

function persistItems(items: FairVariantCompareItem[]) {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch {
    // localStorage may be unavailable
  }
}

export type VariantCompareToggleResult =
  | 'added'
  | 'removed'
  | 'limit'

export const useVariantCompareStore = defineStore('variantCompare', () => {
  const items = ref<FairVariantCompareItem[]>(readStoredItems())

  const count = computed(() => items.value.length)
  const isEmpty = computed(() => items.value.length === 0)
  const isFull = computed(() => items.value.length >= FAIR_VARIANT_COMPARE_MAX)
  const canCompare = computed(() => items.value.length >= 2)

  watch(
    items,
    (next) => {
      persistItems(next)
    },
    { deep: true },
  )

  function has(variantId: number): boolean {
    return items.value.some((item) => item.variantId === variantId)
  }

  function add(item: FairVariantCompareItem): VariantCompareToggleResult {
    if (has(item.variantId)) {
      return 'added'
    }
    if (items.value.length >= FAIR_VARIANT_COMPARE_MAX) {
      return 'limit'
    }
    items.value = [...items.value, item]
    return 'added'
  }

  function remove(variantId: number): void {
    items.value = items.value.filter((item) => item.variantId !== variantId)
  }

  function toggle(item: FairVariantCompareItem): VariantCompareToggleResult {
    if (has(item.variantId)) {
      remove(item.variantId)
      return 'removed'
    }
    return add(item)
  }

  function clear(): void {
    items.value = []
  }

  return {
    items,
    count,
    isEmpty,
    isFull,
    canCompare,
    has,
    add,
    remove,
    toggle,
    clear,
  }
})
